import { renderFreezeFrame } from "@viewer-freeze-capture";
import { resolveFramePosition } from "@viewer-freeze-metadata";
import { footprintCentre, parseFootprintJSON, type FootprintData } from "@viewer-footprint";
import type { ApiClient } from "../api/client";
import { fetchTrack } from "../export/track";
import {
  CaptureFatalError, capturePoiFrames,
  type CaptureApi, type CapturePoiFramesResult, type FrameGrabber,
} from "./poiCapture";
import { createVideoGrabber, type VideoGrabberOpts } from "@viewer-grabber";

export interface RunPoiCaptureInput {
  apiClient: Pick<ApiClient, "getFlight">;
  annotationsApi: CaptureApi;
  flightId: string;
  team?: string;
  reviewer: { reviewerId: string; displayName: string };
  authToken?: string;
  workerOrigin: string;
  signal?: AbortSignal;
  onProgress?: (done: number, total: number) => void;
  /** Test seam; the real one opens an off-screen video. */
  createGrabber?: (opts: VideoGrabberOpts) => Promise<FrameGrabber>;
}

/** The footprint is a nicety here: without it the aircraft position stands in. */
async function loadFootprint(url: string | undefined): Promise<FootprintData | null> {
  if (url === undefined) return null;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    return parseFootprintJSON(await res.json());
  } catch {
    return null;
  }
}

/**
 * Save a frame for every POI on the flight that has none. Fetches the flight
 * detail afresh because the page may have sat open past its presigned URLs'
 * expiry, and always tears the off-screen video down.
 */
export async function runPoiCapture(input: RunPoiCaptureInput): Promise<CapturePoiFramesResult> {
  const { flightId, team } = input;
  const detail = await input.apiClient.getFlight(flightId, { team });
  const startMs = Date.parse(detail.flight.videoStartUtc ?? "");
  if (!Number.isFinite(startMs)) {
    throw new CaptureFatalError("This flight has no start time, so its frames can't be stamped.");
  }
  const [track, footprint] = await Promise.all([
    fetchTrack(detail.urls.track),
    loadFootprint(detail.urls.footprint),
  ]);
  const grabber = await (input.createGrabber ?? createVideoGrabber)({
    videoUrl: detail.urls.video,
    manifestUrl: detail.urls.manifest,
    authToken: input.authToken,
    workerOrigin: input.workerOrigin,
  });
  try {
    return await capturePoiFrames({
      flightId, team,
      videoStartUtcS: startMs / 1000,
      tz: detail.flight.displayTimezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
      track,
      centreAt: (t) => (footprint === null ? null : footprintCentre(footprint, t)),
      reviewer: input.reviewer,
      grabber,
      render: renderFreezeFrame,
      resolvePosition: resolveFramePosition,
      api: input.annotationsApi,
      signal: input.signal,
      onProgress: input.onProgress,
    });
  } finally {
    grabber.dispose();
  }
}
